import "~/ui/registerWebComponents";
import { bindStatsigEventAttributes, getStatsigUser, initStatsig, logEvent } from "~/integrations/statsig";
const getMarketingQueryParams = () => new URLSearchParams(window.location.search);
const parsePositiveInt = (value) => {
    if (!value) {
        return null;
    }
    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};
const sanitizeNumericValue = (value, maxDigits) => value.replace(/\D/g, "").slice(0, maxDigits);
const TRACKING_PASSTHROUGH_SELECTOR = "[data-tracking-passthrough]";
const TRACKING_PASSTHROUGH_PARAM_NAMES = new Set(["s1", "s2", "s3", "s4", "gbraid", "s5"]);
const MARKETING_STATSIG_PAGE_VIEWED_EVENT = "Marketing: Page Viewed";
const getTrackingPassthroughParams = (params) => {
    const mapped = [];
    const gclid = params.get("gclid");
    const s2 = params.get("s2");
    const s3 = params.get("s3");
    const gadCampaignId = params.get("gad_campaignid");
    const gbraid = params.get("gbraid");
    const wbraid = params.get("wbraid");
    const braid = gbraid || wbraid;
    if (gclid) {
        mapped.push(["s1", gclid]);
    }
    if (s2) {
        mapped.push(["s2", s2]);
    }
    if (s3) {
        mapped.push(["s3", s3]);
    }
    if (gadCampaignId) {
        mapped.push(["s4", gadCampaignId]);
    }
    if (gbraid) {
        mapped.push(["gbraid", gbraid]);
    }
    if (braid) {
        mapped.push(["s5", braid]);
    }
    return mapped;
};
const appendTrackingPassthroughParams = (url, mappedParams) => {
    if (!mappedParams.length) {
        return;
    }
    const out = new URLSearchParams();
    for (const [key, value] of url.searchParams.entries()) {
        if (!TRACKING_PASSTHROUGH_PARAM_NAMES.has(key)) {
            out.append(key, value);
        }
    }
    mappedParams.forEach(([key, value]) => out.append(key, value));
    url.search = out.toString();
};
const applyMarketingDynamicHeadline = (headline, params) => {
    const paramName = headline.dataset.marketingHeadlineParam || "hl";
    const fallbackHeadline = headline.dataset.marketingHeadlineFallback || headline.textContent?.trim() || "";
    const customHeadline = params.get(paramName)?.trim();
    headline.textContent = customHeadline || fallbackHeadline;
};
const bindMarketingZipRedirect = (form, trackingPassthroughParams) => {
    if (form.dataset.marketingZipBound === "1") {
        return;
    }
    const zipInput = form.querySelector("[data-marketing-zip-input]") ||
        form.querySelector('input[name="zip"]');
    if (!zipInput) {
        return;
    }
    const maxDigits = parsePositiveInt(form.dataset.marketingZipMaxDigits || zipInput.getAttribute("maxlength") || undefined) || 6;
    const queryParam = form.dataset.marketingZipParam || zipInput.name || "zip";
    const redirectPath = form.dataset.marketingZipRedirect || form.getAttribute("action") || "/apply/";
    const shouldPassTracking = form.matches(TRACKING_PASSTHROUGH_SELECTOR);
    const sanitizeZip = (value) => sanitizeNumericValue(value, maxDigits);
    form.dataset.marketingZipBound = "1";
    zipInput.inputMode = "numeric";
    zipInput.maxLength = maxDigits;
    zipInput.pattern = `[0-9]{1,${maxDigits}}`;
    zipInput.addEventListener("input", () => {
        zipInput.value = sanitizeZip(zipInput.value);
    });
    form.addEventListener("submit", (event) => {
        event.preventDefault();
        const zip = sanitizeZip(zipInput.value);
        zipInput.value = zip;
        if (!zip) {
            zipInput.focus();
            return;
        }
        const redirectUrl = new URL(redirectPath, window.location.href);
        if (shouldPassTracking) {
            appendTrackingPassthroughParams(redirectUrl, trackingPassthroughParams);
        }
        redirectUrl.searchParams.set(queryParam, zip);
        window.location.assign(redirectUrl.toString());
    });
};
const applyMarketingTrackingPassthrough = (element, mappedParams) => {
    if (!mappedParams.length) {
        return;
    }
    const href = element.getAttribute("href");
    if (!href) {
        return;
    }
    const url = new URL(href, window.location.href);
    appendTrackingPassthroughParams(url, mappedParams);
    element.setAttribute("href", url.toString());
};
const bindMarketingStickyAfter = (element) => {
    if (element.dataset.marketingStickyAfterBound === "1") {
        return;
    }
    const targetSelector = element.dataset.marketingStickyAfter;
    if (!targetSelector) {
        return;
    }
    const target = document.querySelector(targetSelector);
    if (!target) {
        return;
    }
    const setVisibility = (isVisible) => {
        element.dataset.visible = isVisible ? "true" : "false";
    };
    element.dataset.marketingStickyAfterBound = "1";
    if (!("IntersectionObserver" in window)) {
        setVisibility(true);
        return;
    }
    const observer = new IntersectionObserver(([entry]) => {
        const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight;
        setVisibility(entry.boundingClientRect.bottom <= viewportBottom);
    }, { threshold: 1 });
    observer.observe(target);
};
const initMarketingStatsigSessionReplay = async () => {
    const root = document.documentElement;
    if (root.dataset.statsigSessionReplay !== "true") {
        return;
    }
    bindStatsigEventAttributes();
    const pageId = root.dataset.pageId || "marketing";
    try {
        const response = await fetch("/api/config/", { credentials: "same-origin" });
        if (!response.ok) {
            throw new Error(`Statsig config request failed with ${response.status}`);
        }
        const config = await response.json();
        const statsigConfig = config?.statsig;
        if (!statsigConfig?.enabled || !statsigConfig?.clientKey) {
            return;
        }
        await Promise.race([
            initStatsig({
                clientKey: statsigConfig.clientKey,
                environmentTier: statsigConfig.environmentTier || "staging",
                user: getStatsigUser({
                    custom: {
                        pageType: "marketing",
                        pageId,
                        path: window.location.pathname,
                    },
                }),
                sessionReplay: {
                    enabled: true,
                    options: {
                        rrwebConfig: {
                            blockSelector: "[data-statsig-replay-block]",
                            inlineStylesheet: false,
                            maskAllInputs: false,
                            maskTextSelector: "[data-statsig-replay-mask]",
                        },
                    },
                },
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Statsig init timeout")), 2500)),
        ]);
        logEvent(MARKETING_STATSIG_PAGE_VIEWED_EVENT, pageId, {
            page_id: pageId,
            path: window.location.pathname,
            session_replay_enabled: true,
        });
    }
    catch (error) {
        console.warn("Statsig Session Replay initialization failed", error);
    }
};
export const initMarketingShared = () => {
    const queryParams = getMarketingQueryParams();
    const trackingPassthroughParams = getTrackingPassthroughParams(queryParams);
    document.querySelectorAll("[data-marketing-headline-param]").forEach((headline) => {
        applyMarketingDynamicHeadline(headline, queryParams);
    });
    document.querySelectorAll("[data-marketing-zip-form]").forEach((form) => {
        bindMarketingZipRedirect(form, trackingPassthroughParams);
    });
    document.querySelectorAll("[data-tracking-passthrough][href]").forEach((element) => {
        applyMarketingTrackingPassthrough(element, trackingPassthroughParams);
    });
    initMarketingStatsigSessionReplay();
    const stickyAfterElements = document.querySelectorAll("[data-marketing-sticky-after]");
    if (!stickyAfterElements.length) {
        return;
    }
    stickyAfterElements.forEach((element) => {
        bindMarketingStickyAfter(element);
    });
};
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMarketingShared);
}
else {
    initMarketingShared();
}
